/**
 * Advanced Backtest Analysis Utilities
 * Professional metrics, risk analysis, and overfitting detection
 */

export const calculateAdvancedMetrics = (result) => {
  if (!result || !result.trades || result.trades.length === 0) {
    return null;
  }

  const { trades, initial_capital, final_capital, winning_trades, losing_trades } = result;
  
  // Calculate Profit Factor
  const totalWins = trades
    .filter(t => t.profit_loss > 0)
    .reduce((sum, t) => sum + t.profit_loss, 0);
  
  const totalLosses = Math.abs(trades
    .filter(t => t.profit_loss < 0)
    .reduce((sum, t) => sum + t.profit_loss, 0));
  
  const profitFactor = totalLosses > 0 ? (totalWins / totalLosses) : totalWins > 0 ? 999 : 0;
  
  // Calculate Risk/Reward Ratio
  const avgWin = winning_trades > 0 ? totalWins / winning_trades : 0;
  const avgLoss = losing_trades > 0 ? totalLosses / losing_trades : 0;
  const riskRewardRatio = avgLoss > 0 ? (avgWin / avgLoss) : avgWin > 0 ? 999 : 0;
  
  // Calculate Sharpe Ratio (simplified)
  const returns = trades.map(t => t.profit_loss_percent);
  const avgReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
  const stdDev = Math.sqrt(
    returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / returns.length
  );
  const sharpeRatio = stdDev > 0 ? (avgReturn / stdDev) : 0;
  
  // Calculate Max Consecutive Wins/Losses
  let maxConsecutiveWins = 0;
  let maxConsecutiveLosses = 0;
  let currentWins = 0;
  let currentLosses = 0;
  
  trades.forEach(trade => {
    if (trade.profit_loss > 0) {
      currentWins++;
      currentLosses = 0;
      maxConsecutiveWins = Math.max(maxConsecutiveWins, currentWins);
    } else {
      currentLosses++;
      currentWins = 0;
      maxConsecutiveLosses = Math.max(maxConsecutiveLosses, currentLosses);
    }
  });
  
  // Calculate Recovery Factor
  const totalProfit = final_capital - initial_capital;
  const maxDrawdownAmount = (result.max_drawdown / 100) * initial_capital;
  const recoveryFactor = maxDrawdownAmount > 0 ? (totalProfit / maxDrawdownAmount) : 0;
  
  return {
    profitFactor: profitFactor.toFixed(2),
    riskRewardRatio: riskRewardRatio.toFixed(2),
    sharpeRatio: sharpeRatio.toFixed(2),
    maxConsecutiveWins,
    maxConsecutiveLosses,
    recoveryFactor: recoveryFactor.toFixed(2),
    avgWin: avgWin.toFixed(2),
    avgLoss: avgLoss.toFixed(2),
    totalWins: totalWins.toFixed(2),
    totalLosses: totalLosses.toFixed(2)
  };
};

export const analyzePeriodicPerformance = (trades) => {
  if (!trades || trades.length === 0) return { monthly: [], quarterly: [] };
  
  const monthlyData = {};
  const quarterlyData = {};
  
  trades.forEach(trade => {
    const date = new Date(trade.entry_time);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    const quarterKey = `${date.getFullYear()}-Q${quarter}`;
    
    // Monthly aggregation
    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = {
        period: monthKey,
        trades: 0,
        wins: 0,
        losses: 0,
        profit: 0,
        winRate: 0
      };
    }
    
    monthlyData[monthKey].trades++;
    monthlyData[monthKey].profit += trade.profit_loss;
    if (trade.profit_loss > 0) {
      monthlyData[monthKey].wins++;
    } else {
      monthlyData[monthKey].losses++;
    }
    
    // Quarterly aggregation
    if (!quarterlyData[quarterKey]) {
      quarterlyData[quarterKey] = {
        period: quarterKey,
        trades: 0,
        wins: 0,
        losses: 0,
        profit: 0,
        winRate: 0
      };
    }
    
    quarterlyData[quarterKey].trades++;
    quarterlyData[quarterKey].profit += trade.profit_loss;
    if (trade.profit_loss > 0) {
      quarterlyData[quarterKey].wins++;
    } else {
      quarterlyData[quarterKey].losses++;
    }
  });
  
  // Calculate win rates
  Object.values(monthlyData).forEach(m => {
    m.winRate = m.trades > 0 ? ((m.wins / m.trades) * 100).toFixed(1) : 0;
    m.profit = m.profit.toFixed(2);
  });
  
  Object.values(quarterlyData).forEach(q => {
    q.winRate = q.trades > 0 ? ((q.wins / q.trades) * 100).toFixed(1) : 0;
    q.profit = q.profit.toFixed(2);
  });
  
  return {
    monthly: Object.values(monthlyData).sort((a, b) => a.period.localeCompare(b.period)),
    quarterly: Object.values(quarterlyData).sort((a, b) => a.period.localeCompare(b.period))
  };
};

export const detectOverfitting = (result) => {
  const warnings = [];
  
  if (!result || !result.trades) return warnings;
  
  const { total_trades, win_rate, max_drawdown, total_return } = result;
  
  // Warning 1: Too few trades
  if (total_trades < 20) {
    warnings.push({
      type: 'low_sample',
      severity: 'high',
      message: '⚠️ Çok az işlem sayısı (< 20). Sonuçlar istatistiksel olarak anlamlı olmayabilir.',
      recommendation: 'Daha uzun bir test periyodu kullanın veya daha sık sinyal üreten bir strateji deneyin.'
    });
  }
  
  // Warning 2: Unrealistic win rate with high drawdown
  if (win_rate > 80 && max_drawdown > 20) {
    warnings.push({
      type: 'unrealistic_winrate',
      severity: 'high',
      message: '⚠️ Yüksek kazanma oranı (%' + win_rate.toFixed(0) + ') ancak yüksek drawdown (%' + max_drawdown.toFixed(0) + ').',
      recommendation: 'Bu strateji geçmiş veriye aşırı uyum sağlamış olabilir (overfitting). Canlı piyasada daha düşük performans gösterebilir.'
    });
  }
  
  // Warning 3: Too good to be true
  if (win_rate > 90 && total_return > 100) {
    warnings.push({
      type: 'too_good',
      severity: 'critical',
      message: '🚨 Son derece yüksek kazanma oranı ve getiri.',
      recommendation: 'Bu sonuçlar gerçekçi olmayabilir. Lookahead bias veya curve fitting olabilir. Stratejinizi tekrar gözden geçirin.'
    });
  }
  
  // Warning 4: High complexity with perfect results
  const trades = result.trades || [];
  const uniqueIndicators = new Set(trades.map(t => t.signal_type)).size;
  
  if (uniqueIndicators > 5 && win_rate > 85) {
    warnings.push({
      type: 'high_complexity',
      severity: 'medium',
      message: '⚠️ Çok fazla parametre kullanımı ile yüksek kazanma oranı.',
      recommendation: 'Karmaşık stratejiler overfitting riskini artırır. Daha basit bir strateji deneyin.'
    });
  }
  
  // Warning 5: Inconsistent performance
  const periodicPerf = analyzePeriodicPerformance(trades);
  if (periodicPerf.monthly.length > 3) {
    const profitableMonths = periodicPerf.monthly.filter(m => parseFloat(m.profit) > 0).length;
    const consistencyRate = (profitableMonths / periodicPerf.monthly.length) * 100;
    
    if (consistencyRate < 40) {
      warnings.push({
        type: 'inconsistent',
        severity: 'medium',
        message: '⚠️ Düzensiz performans. Sadece %' + consistencyRate.toFixed(0) + ' ay karlı.',
        recommendation: 'Bu strateji belirli piyasa koşullarında çalışıyor olabilir. Farklı piyasa dönemlerinde test edin.'
      });
    }
  }
  
  return warnings;
};

export const generateEquityWithDrawdown = (equityCurve) => {
  if (!equityCurve || equityCurve.length === 0) return [];
  
  let peak = equityCurve[0].capital;
  
  return equityCurve.map(point => {
    if (point.capital > peak) {
      peak = point.capital;
    }
    
    const drawdown = peak > 0 ? ((peak - point.capital) / peak) * 100 : 0;
    
    return {
      ...point,
      peak,
      drawdown: drawdown.toFixed(2),
      drawdownAmount: (peak - point.capital).toFixed(2)
    };
  });
};

export const BACKTEST_DISCLAIMERS = {
  main: '⚠️ Backtest sonuçları geçmiş verilere dayalı simülasyondur. Gerçek piyasa sonuçları farklı olabilir.',
  lookahead: '✓ Lookahead Bias: YOK - Tüm sinyaller mum kapanışı sonrası üretilmiştir.',
  execution: '📊 Emir Simülasyonu: Bir sonraki mum açılış fiyatı',
  slippage: '⚡ Slippage ve komisyonlar hesaba katılmamıştır.',
  risk: '🔴 Geçmiş performans gelecek sonuçları garanti etmez.',
};

export const getMetricColor = (value, type) => {
  switch (type) {
    case 'winRate':
      if (value >= 60) return 'text-green-400';
      if (value >= 50) return 'text-yellow-400';
      return 'text-red-400';
    
    case 'profitFactor':
      if (value >= 2) return 'text-green-400';
      if (value >= 1.5) return 'text-yellow-400';
      return 'text-red-400';
    
    case 'sharpe':
      if (value >= 1.5) return 'text-green-400';
      if (value >= 1) return 'text-yellow-400';
      return 'text-red-400';
    
    case 'drawdown':
      if (value <= 10) return 'text-green-400';
      if (value <= 20) return 'text-yellow-400';
      return 'text-red-400';
    
    default:
      return 'text-slate-300';
  }
};
